import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { CircleX } from "lucide-react";

const BulkUploadErrorModal = ({ onClose, errorMsgs = [] }) => {

  const compressRanges = (numbers) => {
    if (!Array.isArray(numbers) || numbers.length === 0) return '';

    const sorted = [...new Set(numbers)]
      .filter(n => typeof n === 'number')
      .sort((a, b) => a - b);

    const ranges = [];
    let start = sorted[0];
    let end = start;

    for (let i = 1; i <= sorted.length; i++) {
      const current = sorted[i];
      if (current === end + 1) {
        end = current;
      } else {
        if (start === end) {
          ranges.push(start.toString());
        } else {
          ranges.push(`${start}-${end}`);
        }
        start = current;
        end = current;
      }
    }

    return ranges.join(', ');
  }


  const { groupedErrors } = useMemo(() => {
    if (!errorMsgs || !Array.isArray(errorMsgs)) {
      return {
        groupedErrors: {},
        displayErrors: "An error occurred while processing your upload. Please try again."
      };
    }
    const groupedErrors = {};

    errorMsgs.forEach(({ rowNumber, errorMessage }) => {
      if (errorMessage) {
        if (!groupedErrors[errorMessage]) {
          groupedErrors[errorMessage] = [];
        }
        groupedErrors[errorMessage].push(rowNumber + 1 || 'N/A');
      }
    });

    return {
      groupedErrors: groupedErrors,
    };
  }, [errorMsgs]);

  return (
    <motion.div
      className="fixed inset-0 z-[1000] bg-black bg-opacity-40 flex items-center justify-center px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-fluid-4 space-y-fluid-4"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* Big Icon */}
        <div className="flex flex-col items-center space-y-fluid-1.5">
          <div className="bg-red-100 rounded-full p-fluid-3">
            <CircleX size={48} className="text-red-600" />
          </div>

          <div className="flex flex-col items-center space-y-fluid-1.5 pb-fluid-4">
            <h2 className="text-fluid-xl font-semibold text-center text-[#111827]">
              Row Upload Errors. Please upload again.
            </h2>
            <h3 className="text-gray-600 text-center">
              Some rows were not properly added. Please review errors below and upload the file again.
            </h3>
          </div>
    
          <div className="max-h-[60vh] overflow-y-auto w-full">
            <table className="min-w-full table-auto border-t border-gray-200">
              <thead className="bg-gray-100 text-left text-sm font-medium text-gray-700">
                <tr>
                  <th className="px-6 py-3 border-b w-1/3">Row Number(s)</th>
                  <th className="px-6 py-3 border-b">Error Message</th>
                </tr>
              </thead>
              <tbody className="text-sm text-gray-800">
                {Object.entries(groupedErrors).map(([message, rows], i) => (
                  <tr key={i} className="odd:bg-white even:bg-gray-50 align-top">
                    <td className="px-6 py-3 border-b whitespace-normal">{compressRanges(rows)}</td>
                    <td className="px-6 py-3 border-b whitespace-pre-line">{message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        {/* Footer with Actions */}
        <div className="flex flex-col items-center gap-fluid-3">
          <div className="flex gap-fluid-2">
            <button
              onClick={onClose}
              className="px-fluid-button-x py-fluid-button-y bg-custom-bg-blue text-white hover:bg-[#1a2a61] text-fluid-sm rounded font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View Contracts
            </button>
          </div>

        </div>
      </motion.div>
    </motion.div>
  );
};

export default BulkUploadErrorModal;
